// Student Assignments: view assigned assignments, upload worksheet, submit, see status

import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Layout } from "../../components/layout/Layout";
import { PageHeader } from "../../components/layout/PageHeader";
import { Card, CardContent } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/Badge";
import { Textarea } from "../../components/ui/Textarea";
import { Modal } from "../../components/ui/Modal";
import { ArrowLeft, FileText, Upload, CheckCircle, RefreshCw, Award, ChevronDown, ChevronUp } from "lucide-react";
import { showSuccessToast, showErrorToast } from "../../utils/errorHandler";
import {
  listAssignments,
  listSubmissions,
  submitAssignment,
  openAssignmentAttachmentFile,
  type AssignmentDTO,
  type SubmissionDTO,
  coerceValidationStaffRubric,
  coerceValidationStaffSuggestions,
  pickValidationStudentMessage,
  pickValidationStudentScore,
  type SubmissionOcrMetadataSuggestionDTO,
} from "../../modules/learning/api/assignments";
import { getStudentMe } from "../../modules/roster/api/roster.client";
import { useFeatureAccess } from "../../contexts/FeatureAccessContext";
import { CommunicationAssignmentsPanel } from "./CommunicationAssignmentsPanel";
import { isSubmissionPdf } from "../../utils/submissionPreview";
import { SubmissionImageComparison } from "@/components/teacher/SubmissionImageComparison";

/** Allowed upload types — must match learning-service `submissions` route (suffix + validation). */
const ALLOWED_SUBMISSION_EXTENSIONS = new Set([".pdf", ".jpg", ".jpeg", ".png", ".webp"]);
const SUBMISSION_FILE_ACCEPT =
  "application/pdf,.pdf,image/jpeg,.jpg,.jpeg,image/png,.png,image/webp,.webp";

function submissionFileExtension(name: string): string {
  const i = name.lastIndexOf(".");
  if (i < 0) return "";
  return name.slice(i).toLowerCase();
}

function isAllowedSubmissionFile(file: File): boolean {
  const ext = submissionFileExtension(file.name);
  if (ext && ALLOWED_SUBMISSION_EXTENSIONS.has(ext)) return true;
  const mime = (file.type || "").toLowerCase().split(";")[0]?.trim() ?? "";
  if (mime === "application/pdf") return true;
  if (mime === "image/jpeg" || mime === "image/png" || mime === "image/webp") return true;
  return false;
}

type StudentAssignmentStatus =
  | "pending"
  | "in_progress"
  | "submitted_for_review"
  | "reviewed";

type StudentAssignmentView = {
  assignment: AssignmentDTO;
  submission: SubmissionDTO | null;
  status: StudentAssignmentStatus;
  submittedAt?: string;
  grade?: number | null;
  feedback?: string | null;
};

const STATUS_LABELS: Record<StudentAssignmentStatus, string> = {
  pending: "Pending",
  in_progress: "In Progress",
  submitted_for_review: "Submitted for Review",
  reviewed: "Reviewed",
};

const STATUS_BADGE_CLASS: Record<StudentAssignmentStatus, string> = {
  pending: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  in_progress: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  submitted_for_review:
    "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
  reviewed: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
};

/** True when the teacher has left a review worth showing (handles legacy rows where status was not updated). */
function hasTeacherReleasedReview(sub: SubmissionDTO): boolean {
  if (sub.reviewed_at != null && String(sub.reviewed_at).trim() !== "") return true;
  if (sub.reviewed_by_user_id != null && String(sub.reviewed_by_user_id).trim() !== "") return true;
  if (sub.grade != null && String(sub.grade).trim() !== "") return true;
  if (sub.feedback != null && String(sub.feedback).trim() !== "") return true;
  return false;
}

function toStudentStatus(sub: SubmissionDTO | null): StudentAssignmentStatus {
  if (!sub) return "pending";
  const s = (sub.status || "").toLowerCase();
  if (s === "published" || s === "reviewed") return "reviewed";
  // Legacy / edge: still "submitted" (or other) but teacher score or feedback already stored
  if (hasTeacherReleasedReview(sub)) return "reviewed";
  if (s === "submitted") return "submitted_for_review";
  return "in_progress";
}

function clampScore(value: number): number {
  return Math.max(0, Math.min(100, value));
}

const SubmissionSuggestionsSection: React.FC<{
  suggestions: SubmissionOcrMetadataSuggestionDTO[];
}> = ({ suggestions }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
      >
        Suggestions ({suggestions.length})
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {expanded && (
        <ul className="mt-3 space-y-2">
          {suggestions.map((s, i) => (
            <li
              key={i}
              className="p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 text-sm"
            >
              {s.original != null && s.original !== "" && (
                <p className="text-slate-600 dark:text-slate-400">
                  <span className="font-medium text-slate-700 dark:text-slate-300">Original: </span>
                  {s.original}
                </p>
              )}
              {s.suggestion != null && s.suggestion !== "" && (
                <p className="text-slate-700 dark:text-slate-300 mt-1">
                  <span className="font-medium text-slate-900 dark:text-white">Suggestion: </span>
                  {s.suggestion}
                </p>
              )}
              {s.reason_tamil != null && s.reason_tamil !== "" && (
                <p className="text-slate-600 dark:text-slate-400 mt-1">{s.reason_tamil}</p>
              )}
              {s.confidence != null && (
                <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">
                  Confidence: {Math.round(s.confidence * 100)}%
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const ASSIGNMENTS_TAB_COMMUNICATION = "communication";

export const StudentAssignmentsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isSchoolFeatureEnabled } = useFeatureAccess();
  const communicationPracticeEnabled = isSchoolFeatureEnabled("communication_tutor");

  const [assignments, setAssignments] = useState<StudentAssignmentView[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAssignment, setSelectedAssignment] = useState<StudentAssignmentView | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [textResponse, setTextResponse] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccessModalOpen, setSubmitSuccessModalOpen] = useState(false);
  const [communicationListRefreshSignal, setCommunicationListRefreshSignal] = useState(0);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      let grade: string | undefined;
      try {
        const me = await getStudentMe();
        grade = me.grade ?? undefined;
      } catch {
        // ignore
      }
      const [assignRes, subRes] = await Promise.all([
        listAssignments({ grade, limit: 100 }),
        listSubmissions({ limit: 100 }),
      ]);
      const subByAsn = new Map<string, SubmissionDTO>();
      for (const s of subRes.items) {
        subByAsn.set(s.assignment_id, s);
      }
      const views: StudentAssignmentView[] = assignRes.items.map((a) => {
        const sub = subByAsn.get(a.assignment_id) ?? null;
        return {
          assignment: a,
          submission: sub,
          status: toStudentStatus(sub),
          submittedAt: sub?.submitted_at
            ? new Date(sub.submitted_at).toLocaleDateString()
            : undefined,
          grade: sub?.grade ?? undefined,
          feedback: sub?.feedback ?? undefined,
        };
      });
      setAssignments(views);
    } catch (err) {
      console.error(err);
      showErrorToast("Failed to load assignments.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const activeTab: "worksheets" | "communication" =
    communicationPracticeEnabled && searchParams.get("tab") === ASSIGNMENTS_TAB_COMMUNICATION
      ? "communication"
      : "worksheets";

  useEffect(() => {
    if (!communicationPracticeEnabled && searchParams.get("tab") === ASSIGNMENTS_TAB_COMMUNICATION) {
      const next = new URLSearchParams(searchParams);
      next.delete("tab");
      setSearchParams(next, { replace: true });
    }
  }, [communicationPracticeEnabled, searchParams, setSearchParams]);

  const setTab = (tab: "worksheets" | "communication") => {
    if (tab === "communication" && !communicationPracticeEnabled) return;
    const next = new URLSearchParams(searchParams);
    if (tab === "communication") next.set("tab", ASSIGNMENTS_TAB_COMMUNICATION);
    else next.delete("tab");
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (activeTab !== "communication") return;
    setSelectedAssignment(null);
    setSelectedFile(null);
    setTextResponse("");
  }, [activeTab]);

  const handleAssignmentsRefresh = () => {
    void loadData();
    setCommunicationListRefreshSignal((n) => n + 1);
  };

  const handleStart = (view: StudentAssignmentView) => {
    if (view.status === "pending") {
      setSelectedAssignment({ ...view, status: "in_progress" });
    } else {
      setSelectedAssignment(view);
    }
    setSelectedFile(null);
    setTextResponse("");
  };

  const handleBack = () => {
    setSelectedAssignment(null);
    setSelectedFile(null);
    setTextResponse("");
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) {
      setSelectedFile(null);
      return;
    }
    if (!isAllowedSubmissionFile(file)) {
      setSelectedFile(null);
      showErrorToast("Only PDF or images are allowed: PDF, JPG, JPEG, PNG, or WebP.");
      return;
    }
    setSelectedFile(file);
  };

  const handleSubmit = async () => {
    if (!selectedAssignment) return;
    const { assignment } = selectedAssignment;
    if (!selectedFile && !textResponse.trim()) {
      showErrorToast("Please write a response or upload a PDF/image.");
      return;
    }
    if (selectedFile && !isAllowedSubmissionFile(selectedFile)) {
      showErrorToast("Only PDF or images are allowed: PDF, JPG, JPEG, PNG, or WebP.");
      setSelectedFile(null);
      return;
    }
    setIsSubmitting(true);
    try {
      await submitAssignment({
        assignment_id: assignment.assignment_id,
        text_response: textResponse.trim() || undefined,
        file: selectedFile ?? undefined,
      });
      setSubmitSuccessModalOpen(true);
      handleBack();
      loadData();
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : "Failed to submit.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = selectedFile || textResponse.trim();

  if (selectedAssignment) {
    const { assignment, submission, status, submittedAt, grade, feedback } = selectedAssignment;
    const isEditable = status === "pending" || status === "in_progress";

    const rubric = coerceValidationStaffRubric(submission?.validation_staff ?? null);
    const extractedText = submission?.validation_staff?.text ?? submission?.ocr_text ?? null;
    const suggestions = coerceValidationStaffSuggestions(submission?.validation_staff ?? null);
    const showSubmissionPreview = Boolean(
      submission?.image_url || submission?.correction_image_url || submission?.text_response
    );
    const showReviewPanel = status === "submitted_for_review" || status === "reviewed";
    const practiceScore =
      submission?.validation_status?.toLowerCase() === "completed"
        ? pickValidationStudentScore(submission.validation_student_summary)
        : null;

    return (
      <Layout>
        <PageHeader title="Assignment" subtitle={assignment.title} />
        <div className="space-y-6">
          <Button
            type="button"
            variant="outline"
            onClick={handleBack}
            className="inline-flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Assignments
          </Button>

          <Card className="border border-slate-200 dark:border-slate-700">
            <CardContent padding="md">
              <div className="space-y-4">
                <div>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                    {assignment.title}
                  </h2>
                  <p className="text-slate-600 dark:text-slate-400">
                    {assignment.title_tamil || assignment.title}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                    {assignment.type}
                  </Badge>
                  <Badge className={STATUS_BADGE_CLASS[status]}>
                    {STATUS_LABELS[status]}
                  </Badge>
                  <span className="text-sm text-slate-600 dark:text-slate-400">
                    Due: {assignment.due_date || "No due date"}
                  </span>
                </div>

                {assignment.instructions && (
                  <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                    <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                      Instructions
                    </h3>
                    <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
                      {assignment.instructions}
                    </p>
                  </div>
                )}

                {(assignment.attachments?.length ?? 0) > 0 && (
                  <div className="p-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/30">
                    <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                      Materials from your teacher
                    </h3>
                    <ul className="space-y-2">
                      {assignment.attachments!.map((a) => (
                        <li key={a.attachment_id}>
                          {a.url ? (
                            <a
                              href={a.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-primary-600 hover:underline break-all"
                            >
                              {a.filename}
                            </a>
                          ) : a.fetch_path ? (
                            <button
                              type="button"
                              onClick={() => void openAssignmentAttachmentFile(a)}
                              className="text-sm text-primary-600 hover:underline break-all text-left"
                            >
                              {a.filename}
                            </button>
                          ) : (
                            <span className="text-sm text-slate-600 dark:text-slate-400">{a.filename}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {isEditable && (
                  <div className="pt-4 border-t border-slate-200 dark:border-slate-700 space-y-4">
                    {/* <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Write your response (optional)
                      </label>
                      <Textarea
                        rows={4}
                        placeholder="Type your response here..."
                        value={textResponse}
                        onChange={(e) => setTextResponse(e.target.value)}
                        className="w-full"
                      />
                    </div> */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Upload handwritten PDF or image
                      </label>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                        Allowed file types only: PDF, JPG, JPEG, PNG, or WebP (max size set by your school).
                      </p>
                      <div className="flex flex-col sm:flex-row gap-4 items-start">
                        <label className="flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600 hover:border-primary-500 dark:hover:border-primary-500 cursor-pointer transition-colors">
                          <Upload className="w-5 h-5 text-slate-500" />
                          <span className="text-sm text-slate-600 dark:text-slate-400">
                            {selectedFile ? selectedFile.name : "Choose file..."}
                          </span>
                          <input
                            type="file"
                            accept={SUBMISSION_FILE_ACCEPT}
                            className="sr-only"
                            onChange={handleFileChange}
                          />
                        </label>
                        <Button
                          type="button"
                          onClick={handleSubmit}
                          disabled={!canSubmit || isSubmitting}
                          className="bg-primary-600 hover:bg-primary-700 !text-white"
                        >
                          {isSubmitting ? "Submitting..." : "Submit"}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {submission && (showSubmissionPreview || showReviewPanel) && (
                  <>
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 pt-4 border-t border-slate-200 dark:border-slate-700">
                      {/* Left: Your Submission */}
                      <div className="space-y-4">
                        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                          Your Submission
                        </h3>
                        {submission.image_url &&
                        isSubmissionPdf(submission.filename, submission.image_url) ? (
                          <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-4 space-y-3">
                            <p className="text-sm text-slate-600 dark:text-slate-400 flex items-center gap-2">
                              <FileText className="w-5 h-5 flex-shrink-0 text-slate-500" />
                              PDF files cannot be previewed inline here. Open your submission in a new tab.
                            </p>
                            <a
                              href={submission.image_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700"
                            >
                              Open submitted PDF
                            </a>
                          </div>
                        ) : (
                          <SubmissionImageComparison
                            originalImageUrl={submission.image_url}
                            correctionImageUrl={submission.correction_image_url}
                            originalLabel="Original submission"
                            correctionLabel="Teacher correction"
                            className="w-full"
                          />
                        )}
                        {submission.text_response && (
                          <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                            <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
                              {submission.text_response}
                            </p>
                          </div>
                        )}
                        {!submission.image_url &&
                          !submission.correction_image_url &&
                          !submission.text_response && (
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                              No submission preview available.
                            </p>
                          )}
                      </div>

                      {/* Right: Review Summary / Rubric Score */}
                      <div className="space-y-4">
                        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                          Review Summary
                        </h3>
                        {showReviewPanel && (
                          <>
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                              {status === "submitted_for_review"
                                ? "Your assignment has been submitted. Your teacher will review it soon."
                                : "Your teacher has reviewed your submission."}
                            </p>
                            {submittedAt && (
                              <p className="text-sm text-slate-500 dark:text-slate-400">Submitted on {submittedAt}</p>
                            )}
                            {status === "reviewed" && grade != null && (
                              <p className="text-lg font-semibold text-slate-900 dark:text-white">
                                Score: {grade}/100
                              </p>
                            )}
                            {status === "reviewed" &&
                              feedback != null &&
                              String(feedback).trim() !== "" && (
                              <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                                <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                  Teacher feedback:
                                </p>
                                <p className="text-slate-600 dark:text-slate-400 mt-1">{feedback}</p>
                              </div>
                            )}
                            {submission &&
                              submission.validation_status?.toLowerCase() === "completed" &&
                              (pickValidationStudentMessage(submission.validation_student_summary) != null ||
                                pickValidationStudentScore(submission.validation_student_summary) != null) && (
                                <div className="p-3 rounded-lg border border-indigo-100 dark:border-indigo-900/40 bg-indigo-50/80 dark:bg-indigo-950/20">
                                  <p className="text-xs font-semibold text-indigo-800 dark:text-indigo-200 uppercase tracking-wide mb-1">
                                    Automatic check (not your final grade)
                                  </p>
                                  {pickValidationStudentScore(submission.validation_student_summary) != null && (
                                    <p className="text-sm font-semibold text-indigo-900 dark:text-indigo-100 mb-2">
                                      Practice score:{" "}
                                      {pickValidationStudentScore(submission.validation_student_summary)}/100
                                    </p>
                                  )}
                                  {pickValidationStudentMessage(submission.validation_student_summary) && (
                                    <p className="text-sm text-indigo-900 dark:text-indigo-100 whitespace-pre-wrap">
                                      {pickValidationStudentMessage(submission.validation_student_summary)}
                                    </p>
                                  )}
                                </div>
                              )}
                            {submission?.validation_status === "failed" &&
                              !pickValidationStudentMessage(submission.validation_student_summary) && (
                                <p className="text-sm text-slate-500 dark:text-slate-400">
                                  An automatic check could not finish for this submission. Your teacher will still
                                  review your work.
                                </p>
                              )}
                          </>
                        )}
                        {rubric && (
                          <div className="p-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 space-y-4">
                            <div className="flex items-center gap-2">
                              <Award className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                              <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                                Scan / handwriting rubric (OCR)
                              </span>
                            </div>
                            {practiceScore != null && (
                              <p className="text-xs text-slate-500 dark:text-slate-400 -mt-2">
                                This is from the proofread step on your file, not the same as the automatic answer check
                                above. A low score here does not mean your teacher marked you wrong.
                              </p>
                            )}
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-slate-700 dark:text-slate-300">Overall Score</span>
                              <span className="text-xl font-semibold text-slate-900 dark:text-white">
                                {Math.round(clampScore(rubric.overallScore ?? 0))}/100
                              </span>
                            </div>
                            {[
                              { key: "grammar" as const, label: "Grammar", color: "bg-emerald-500" },
                              { key: "clarity" as const, label: "Clarity", color: "bg-sky-500" },
                              { key: "vocabulary" as const, label: "Vocabulary", color: "bg-violet-500" },
                            ].map(({ key, label, color }) => {
                              const value = rubric[key] ?? 0;
                              return (
                                <div key={key} className="space-y-1">
                                  <div className="flex items-center justify-between text-sm">
                                    <span className="text-slate-700 dark:text-slate-300">{label}</span>
                                    <span className="font-medium text-slate-900 dark:text-slate-100">
                                      {Math.round(clampScore(value))}/100
                                    </span>
                                  </div>
                                  <div className="h-2 rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden">
                                    <div
                                      className={`h-full rounded-full ${color}`}
                                      style={{ width: `${clampScore(value)}%` }}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                            {rubric.feedback && (
                              <div className="pt-2 border-t border-slate-200 dark:border-slate-700">
                                <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">
                                  Feedback
                                </p>
                                <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
                                  {rubric.feedback}
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                        {submission.ocr_score != null && (
                          <div className="text-sm text-slate-600 dark:text-slate-400 space-y-1">
                            <p>
                              Handwriting scan score (OCR):{" "}
                              <span className="font-semibold text-slate-900 dark:text-white">
                                {submission.ocr_score}/100
                              </span>
                            </p>
                            {practiceScore != null && submission.ocr_score === 0 && (
                              <p className="text-xs text-slate-500 dark:text-slate-400">
                                This number comes only from the scan/proofread service (e.g. rubric on your writing),
                                not from comparing your answers to the answer key. Use the practice score for how
                                well your answers matched the assignment.
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Below grid: Extracted text */}
                    {/* {extractedText && (
                      <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
                        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                          Extracted Text
                        </h3>
                        <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                          <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
                            {extractedText}
                          </p>
                        </div>
                      </div>
                    )} */}

                    {/* Below grid: Suggestions */}
                    {suggestions.length > 0 && (
                      <SubmissionSuggestionsSection suggestions={suggestions} />
                    )}
                  </>
                )}

              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
        <PageHeader
          title="My Assignments"
          subtitle={
            activeTab === "communication"
              ? "Communication practice tasks from your teacher"
              : "View and submit your assigned worksheets"
          }
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => handleAssignmentsRefresh()}
          disabled={loading && activeTab === "worksheets"}
          className="inline-flex items-center justify-center gap-2 flex-shrink-0"
        >
          <RefreshCw className={loading ? "w-4 h-4 animate-spin" : "w-4 h-4"} />
          Refresh
        </Button>
      </div>

      {communicationPracticeEnabled && (
        <div className="flex gap-2 border-b border-slate-200 dark:border-slate-700 mb-6" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "worksheets"}
            className={`px-4 py-2 text-sm font-semibold rounded-t-lg border-b-2 transition-colors ${
              activeTab === "worksheets"
                ? "border-primary-600 text-primary-700 dark:text-primary-300"
                : "border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200"
            }`}
            onClick={() => setTab("worksheets")}
          >
            Worksheets
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "communication"}
            className={`px-4 py-2 text-sm font-semibold rounded-t-lg border-b-2 transition-colors ${
              activeTab === "communication"
                ? "border-primary-600 text-primary-700 dark:text-primary-300"
                : "border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200"
            }`}
            onClick={() => setTab("communication")}
          >
            Communication practice
          </button>
        </div>
      )}

      {activeTab === "communication" ? (
        <div className="max-w-3xl">
          <CommunicationAssignmentsPanel refreshSignal={communicationListRefreshSignal} />
        </div>
      ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? (
          <div className="col-span-full text-center py-16 text-slate-600 dark:text-slate-400">
            Loading assignments...
          </div>
        ) : (
          assignments.map((view) => (
            <Card
              key={view.assignment.assignment_id}
              padding="none"
              className="bg-white border border-slate-200 dark:border-slate-700 shadow-lg overflow-hidden hover:shadow-xl transition-shadow cursor-pointer"
              onClick={() => handleStart(view)}
            >
              <CardContent padding="md">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-7 h-7 text-blue-700 dark:text-blue-300" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                      {view.assignment.title}
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      {view.assignment.title_tamil || view.assignment.title}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                        {view.assignment.type}
                      </Badge>
                      <Badge className={STATUS_BADGE_CLASS[view.status]}>
                        {STATUS_LABELS[view.status]}
                      </Badge>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                      Due: {view.assignment.due_date || "No due date"}
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-4 w-full"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStart(view);
                      }}
                    >
                      {view.status === "pending"
                        ? "Start"
                        : view.status === "in_progress"
                          ? "Upload & Submit"
                          : "View"}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
      )}

      {activeTab === "worksheets" && !loading && assignments.length === 0 && (
        <div className="text-center py-16 text-slate-600 dark:text-slate-400">
          No assignments assigned to you yet.
        </div>
      )}

      <Modal
        isOpen={submitSuccessModalOpen}
        onClose={() => setSubmitSuccessModalOpen(false)}
        title="Assignment Submitted"
        showCloseButton
      >
        <div className="flex flex-col items-center gap-4 py-4">
          <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-400" />
          </div>
          <p className="text-center text-slate-700 dark:text-slate-300">
            Assignment submitted successfully. Your teacher will review it.
          </p>
          <Button
            onClick={() => setSubmitSuccessModalOpen(false)}
            className="bg-primary-600 hover:bg-primary-700 !text-white"
          >
            OK
          </Button>
        </div>
      </Modal>
    </Layout>
  );
};
